import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { UserRole, useAuth } from '../hooks/useAuth';
import { Mail, Lock, User } from 'lucide-react';
import TermsModal from '../components/TermsModal';

type AuthMode = 'signin' | 'signup' | 'forgot-password';

export default function Auth() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<AuthMode>(() => {
    // Initialize mode from URL parameter
    return (searchParams.get('mode') as AuthMode) || 'signin';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<UserRole>(() => {
    // Initialize role from URL parameter
    return (searchParams.get('role') as UserRole) || 'reader';
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  
  const { signIn, signInWithGoogle, signUp, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || '/';

  // Update mode when URL parameter changes
  useEffect(() => {
    const newMode = searchParams.get('mode') as AuthMode;
    if (newMode && ['signin', 'signup', 'forgot-password'].includes(newMode)) {
      setMode(newMode);
    }
  }, [searchParams]);

  // Update role when URL parameter changes
  useEffect(() => {
    const newRole = searchParams.get('role') as UserRole;
    if (newRole && ['reader', 'author'].includes(newRole)) {
      setRole(newRole);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (!agreedToTerms) {
          setError('You must agree to the Terms of Service to create an account');
          setLoading(false);
          return;
        }
        const { error } = await signUp(email, password, username, role);
        if (error) throw error;
        setSuccess('Account created successfully! You can now sign in.');
      } else if (mode === 'forgot-password') {
        const { error } = await resetPassword(email);
        if (error) throw error;
        setSuccess('A temporary password has been sent to your email. Please check your inbox and spam folder.');
      } else {
        const { error } = await signIn(email, password);
        if (error) throw error;
        navigate(returnTo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      const { error } = await signInWithGoogle();
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
    }
  };

  return (
    <>
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-center mb-6">
            {mode === 'signin' 
              ? 'Welcome Back!' 
              : mode === 'signup'
              ? 'Create Your Account'
              : 'Reset Password'}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {mode !== 'forgot-password' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            )}

            {mode === 'signup' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="pl-10 w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    I am a...
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setRole('reader')}
                      className={`p-3 text-center rounded-lg border ${
                        role === 'reader'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'border-gray-300 hover:border-indigo-500'
                      }`}
                    >
                      Reader
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole('author')}
                      className={`p-3 text-center rounded-lg border ${
                        role === 'author'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'border-gray-300 hover:border-indigo-500'
                      }`}
                    >
                      Author
                    </button>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="text-gray-700">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setIsTermsModalOpen(true)}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        Terms of Service
                      </button>
                    </label>
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading || (mode === 'signup' && !agreedToTerms)}
              className="w-full bg-indigo-600 text-white p-2 rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading
                ? 'Please wait...'
                : mode === 'signin'
                ? 'Sign In'
                : mode === 'signup'
                ? 'Create Account'
                : 'Reset Password'}
            </button>

            {mode !== 'forgot-password' && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
            )}

            {mode !== 'forgot-password' && (
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-2 p-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                <img 
                  src="https://www.google.com/favicon.ico" 
                  alt="Google" 
                  className="w-5 h-5"
                />
                <span>Google</span>
              </button>
            )}
          </form>

          <div className="mt-4 text-center space-y-2">
            {mode === 'signin' && (
              <button
                onClick={() => setMode('forgot-password')}
                className="text-indigo-600 hover:text-indigo-800 text-sm"
              >
                Forgot your password?
              </button>
            )}
            <div>
              <button
                onClick={() => {
                  const newMode = mode === 'signin' ? 'signup' : 'signin';
                  setMode(newMode);
                  // Update URL without navigating
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('mode', newMode);
                  window.history.replaceState({}, '', `${location.pathname}?${newParams}`);
                }}
                className="text-indigo-600 hover:text-indigo-800"
              >
                {mode === 'signin'
                  ? "Don't have an account? Sign Up"
                  : 'Already have an account? Sign In'}
              </button>
            </div>
            {mode === 'forgot-password' && (
              <button
                onClick={() => setMode('signin')}
                className="text-indigo-600 hover:text-indigo-800"
              >
                Back to Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </>
  );
}